import { ThemeToggle } from "./ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { LayoutDashboardIcon, LayoutGrid, LogOutIcon, MessageCircleHeart, Moon, Settings, Sun, View, CreditCard } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import { useAuth } from "@/contexts/AuthContext";
import { MESSAGES, POSTHOG, ROUTES } from "@/constants";
import { usePostHogTracking } from "@/hooks/usePostHogTracking";
import { signOut } from "@firebase/auth";
import { auth } from "@/config/firebase";
import { useNavigate } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Icon } from "./ui/icon";
import { isLocal } from "@/helpers/cookie.helper";
import { Pulse } from "./icons";
import React from 'react';
import { SubscriptionProgress } from "./subscription/SubscriptionProgress";
import { SubscriptionData } from "@/helpers/subscription.helper";
import { Badge } from "./ui/badge";
import { motion } from "framer-motion";
import { Skeleton } from "./ui/skeleton";
import { useSubscriptionContext } from "@/contexts/SubscriptionContext";

interface UserAvatarProps {
  className?: string;
  /** Compact rail mode — renders only the avatar circle and tucks the theme toggle inside the dropdown. */
  compact?: boolean;
  /** On-band variant — the (non-compact) footer row mounts on the dark nav rail,
   *  so the username + theme-toggle icon switch to band-ink. The avatar circle
   *  stays accent. Light-surface default is unchanged for other mounts. */
  onBand?: boolean;
  /** Overrides the computed userDetails name. Used on the Access Restricted
   *  screen: at that point the user isn't fully signed in (auth failed
   *  before userDetails could populate), but the display name is already
   *  known from the unverified magic-link payload and is safe to show —
   *  it's display-only, not used for authorization. */
  displayName?: string;
  /** Overrides the computed userDetails email — same rationale as displayName. */
  displayEmail?: string;
}

export interface Dropdown{
  header: string | React.ReactNode;
  items: DropdownItem[];
}
export interface DropdownItem{
  id: string;
  type: "COMPONENT" | "DEFAULT" | "NESTED";
  hide?: boolean;
  component?: React.ReactNode;
  label?: string;
  onClick?: () => void;
  items?: DropdownItem[];
  icon?: React.ReactNode 
  // | {
  //   path: string;
  //   size: number;
  //   className: string;
  // };
}

export function UserAvatar({ className, compact = false, onBand = false, displayName, displayEmail }: UserAvatarProps) {
  const navigate = useNavigate();
  const { userDetails, removeAuthTokenCookie, removeSarasGlobalCookie, removeUserDetails, roleAccess, sarasGlobal } =
    useAuth();
  const { refetchUtilisation, subscriptionData, creditsData, subscriptionLoading, utilisationError } = useSubscriptionContext();
  const { theme, toggleTheme } = useTheme();
  const { trackEvent } = usePostHogTracking();

  const handleLogout = () => {
    removeAuthTokenCookie();
    removeSarasGlobalCookie();
    // Clear the user-details cookie (name, email, userId, companyId) so PII
    // does not linger on shared browsers after the user signs out.
    removeUserDetails?.();
    signOut(auth).then(() => {
      navigate("/auth");
    });
  };

  const redirectToProduct = (product: string)=>{
    const pulseUrl = import.meta.env.VITE_PRODUCT_PULSE;
    const sarasUrl = import.meta.env.VITE_PRODUCT_SARAS;
    // "Saras Accounts" is the Daton product entry, so it reports as `daton`.
    trackEvent(POSTHOG.EVENTS.PRODUCT_SWITCHER_CLICKED, {
      product: product === "pulse" ? POSTHOG.PROPERTY_VALUES.PRODUCT.PULSE : POSTHOG.PROPERTY_VALUES.PRODUCT.DATON,
    });
    if(product === 'pulse'){
      window.open(isLocal ? 'http://localhost:4300/login' : pulseUrl, "_blank");
    }else if(product === 'saras'){
      window.open(isLocal ? 'http://localhost:4200/login' : sarasUrl, "_blank");
    }
  }

  const userName = displayName || userDetails?.firstName || sarasGlobal?.user?.firstName || "User";
  const email = displayEmail || userDetails?.email || sarasGlobal?.user?.email || "";

  const avatarDropdown: Dropdown = {
    header: <UserAvatarHeader userName={userName} email={email} className={'w-[100%] py-3 px-3'} subscriptionData={subscriptionData} subscriptionLoading={subscriptionLoading ?? false}/>,
    items: [
      {
        id: "subscription-progress",
        type: "COMPONENT",
        hide: (!creditsData?.progress && !subscriptionLoading) || (!subscriptionData || !subscriptionData?.nextBillingDate),
        component: <SubscriptionProgress creditsData={creditsData} subscriptionData={subscriptionData} isLoading={subscriptionLoading} utilisationError={utilisationError} />,
        onClick: () => {}
      },
      // Admin trio + Settings live ONLY in this account menu (restored per Venkat's
      // mock — the wave-2a rail lower-cluster is removed). The admin trio each keeps
      // its exact RBAC gate; all navigate to the existing /admin/* · /settings inner
      // shell routes.
      {
        id: "godview",
        type: "DEFAULT",
        label: "God View",
        onClick: () => navigate('/admin/godview'),
        hide: !roleAccess?.READ_GODVIEW,
        icon: <View size={16} className="text-[color:var(--dash-muted,var(--text-medium-emphasis))]"/>
      },
      {
        id: "adoption-metrics-dashboard",
        type: "DEFAULT",
        label: "Adoption Metrics",
        onClick: () => navigate('/admin/adoption-metrics-dashboard'),
        hide: !roleAccess?.READ_ADOPTION_METRICS,
        icon: <LayoutDashboardIcon size={16} className="text-[color:var(--dash-muted,var(--text-medium-emphasis))]"/>
      },
      {
        id: "admin-feedback",
        type: "DEFAULT",
        label: "Admin Feedback",
        onClick: () => navigate('/admin/feedback'),
        hide: !roleAccess?.READ_ADMINFEEDBACKLIST,
        icon: <MessageCircleHeart size={16} className="text-[color:var(--dash-muted,var(--text-medium-emphasis))]"/>
      },
      {
        id: "subscription-management",
        type: "DEFAULT",
        label: MESSAGES.SUBSCRIPTION_MANAGEMENT.TITLE,
        onClick: () => navigate(ROUTES.ADMIN_SUBSCRIPTIONS),
        hide: !roleAccess?.READ_GODVIEW,
        icon: <CreditCard size={16} className="text-[color:var(--dash-muted,var(--text-medium-emphasis))]"/>
      },
      // Settings is open to EVERY authenticated user: its Appearance section (the
      // theme picker) is ungated, and Settings itself hides/guards the admin-only
      // sections (User Management, Integrations, Context Console) per-section. So
      // this entry is always shown — non-admins land on Appearance, admins on
      // User Management.
      {
        id: "settings",
        type: "DEFAULT",
        label: "Settings",
        onClick: () => navigate('/settings'),
        icon: <Settings size={16} className="text-[color:var(--dash-muted,var(--text-medium-emphasis))]"/>
      },
      {
        id: "product-switch",
        type: "NESTED",
        label: "Product Switch",
        icon: <LayoutGrid size={16} className="text-[color:var(--dash-muted,var(--text-medium-emphasis))]"/>,
        items: [
          {
            id: "product-switch-saras",
            type: "DEFAULT",
            label: "Saras Accounts",
            onClick: () => redirectToProduct('saras'),
            icon: <Icon icon="saras.svg" size={16} className="size-[16px]" />
            // <SquareArrowUpRight size={12} className="ml-2 size-[12px]" />
          },
          {
            id: "product-switch-pulse",
            type: "DEFAULT",
            label: "Pulse",
            onClick: () => redirectToProduct('pulse'),
            icon: <Pulse size={16} color="var(--semantic-purple-500)"/>
          }
        ]
      },
      {
        id: "theme-toggle",
        type: "DEFAULT",
        label: theme === "light" ? "Dark mode" : "Light mode",
        hide: !compact,
        onClick: () => toggleTheme(),
        icon: theme === "light"
          ? <Moon size={16} className="text-[color:var(--dash-muted,var(--text-medium-emphasis))]" />
          : <Sun size={16} className="text-[color:var(--dash-muted,var(--text-medium-emphasis))]" />,
      },
      {
        id: "logout",
        type: "DEFAULT",
        label: "Log out",
        onClick: handleLogout,
        icon: <LogOutIcon className="h-4 w-4 text-[color:var(--dash-muted,var(--text-medium-emphasis))]" />
      }
    ]
  }

  const onDialogChange = (open: boolean)=>{
    if (open && (creditsData?.progress?.usedCredits !== (creditsData?.progress?.credits || 0))) {
      refetchUtilisation?.()
    }
  }
  if (compact) {
    return (
      <div className={`flex justify-center ${className || ''}`}>
        <DropdownMenu onOpenChange={(open) => onDialogChange(open)}>
          <DropdownMenuTrigger asChild className="cursor-pointer">
            <button
              aria-label="Open user menu"
              className="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--dash-accent,var(--primary-500))] text-[color:var(--dash-band-active-ink,var(--structural-white))] text-sm font-medium cursor-pointer"
            >
              {userName.charAt(0).toUpperCase()}
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            side="top"
            align="start"
            sideOffset={10}
            // Border + elevation come from the dashboard-themes.css dropdown-menu-content
            // slot rule (panel-border + --dash-overlay-shadow) — border/shadow
            // utilities are dead on this slot; see that rule's comment.
            className="w-62 rounded-[10px] bg-[var(--dash-surface,var(--structural-white))] p-1.5 text-[color:var(--dash-ink,var(--text-high-emphasis))]"
          >
            {avatarDropdown.header && (typeof avatarDropdown.header === "string" ? <span>{avatarDropdown.header}</span> : avatarDropdown.header)}
            <DropdownItems items={avatarDropdown.items}/>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className={`flex gap-2 flex-col px-2 py-3 text-left ${className || ''}`}>
      <div className="iq-avatar flex justify-between gap-2">
        <DropdownMenu onOpenChange={(open) => onDialogChange(open)}>
          <DropdownMenuTrigger asChild className="cursor-pointer">
            <UserAvatarHeader userName={userName} email={email} className={'w-[calc(100%-28px)]'} subscriptionData={subscriptionData} subscriptionLoading={subscriptionLoading ?? false} onBand={onBand}/>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            side="top"
            align="end"
            sideOffset={10}
            alignOffset={-75}
            // Border + elevation come from the dashboard-themes.css dropdown-menu-content
            // slot rule (panel-border + --dash-overlay-shadow) — border/shadow
            // utilities are dead on this slot; see that rule's comment.
            className="w-62 rounded-[10px] bg-[var(--dash-surface,var(--structural-white))] p-1.5 text-[color:var(--dash-ink,var(--text-high-emphasis))]"
          >
            {avatarDropdown.header && (typeof avatarDropdown.header === "string" ? <span>{avatarDropdown.header}</span> : avatarDropdown.header)}
            <DropdownItems items={avatarDropdown.items}/>
          </DropdownMenuContent>
        </DropdownMenu>
        <ThemeToggle className={onBand ? "text-[color:color-mix(in_srgb,var(--rail-ink,var(--grey-100))_68%,transparent)]" : "text-icon-grey1"} />
      </div>
    </div>
  );
}

const DropdownItems = React.forwardRef<
  HTMLDivElement,
  { items: DropdownItem[]; className?: string }
>(({ items, className = "mt-1" }, ref) => {
  return (
    <div className={className} ref={ref}>
      {items.map((item) => {
        const itemKey = item.id;

        if (item.hide) return <React.Fragment key={itemKey} />;
        switch (item.type) {
          case "DEFAULT":
            return (
              <DropdownMenuItem className="cursor-pointer rounded-[6px] px-2.5 py-2 text-[13px] font-medium text-[color:var(--dash-ink,var(--text-high-emphasis))] transition-colors duration-150 ease-[var(--dash-ease)] hover:bg-[var(--dash-surface-2,var(--structural-neutral-2))] focus:bg-[var(--dash-surface-2,var(--structural-neutral-2))] motion-reduce:transition-none" key={itemKey} onClick={item.onClick}>
                {item.icon}
                <span>{item.label}</span>
              </DropdownMenuItem>
            );
          case "COMPONENT":
            return (
              item.component ? (
                <DropdownMenuItem className="rounded-[6px] bg-[var(--dash-surface-2,var(--structural-neutral-2))] text-[color:var(--dash-ink,var(--text-high-emphasis))] hover:bg-[var(--dash-surface-2,var(--structural-neutral-2))] focus:bg-[var(--dash-surface-2,var(--structural-neutral-2))]" key={itemKey} onClick={item.onClick}>
                  {item.component}
                </DropdownMenuItem>
              ) : (
                <React.Fragment key={itemKey} />
              )
            );
          case "NESTED":
            return (
              <DropdownMenuSub key={itemKey}>
                <DropdownMenuSubTrigger className="cursor-pointer rounded-[6px] px-2.5 py-2 text-[13px] font-medium text-[color:var(--dash-ink,var(--text-high-emphasis))] transition-colors duration-150 ease-[var(--dash-ease)] hover:bg-[var(--dash-surface-2,var(--structural-neutral-2))] focus:bg-[var(--dash-surface-2,var(--structural-neutral-2))] data-[state=open]:bg-[var(--dash-surface-2,var(--structural-neutral-2))] motion-reduce:transition-none">
                  {item.icon}
                  <span className="cursor-pointer ml-2">{item.label}</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent
                  className="min-w-[11rem] overflow-hidden rounded-[10px] border border-[var(--dash-hairline,var(--grey-200))] bg-[var(--dash-surface,var(--structural-white))] p-1.5 text-[color:var(--dash-ink,var(--text-high-emphasis))] shadow-[var(--dash-shadow)]"
                  sideOffset={10}
                  alignOffset={-5}
                >
                  {!!item?.items?.length && <DropdownItems items={item.items} ref={ref} className="mt-0" />}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            );
          default:
            return null;
        }
      })}
    </div>
  );
});


const getColorClasses = (status: 'success' | 'error' | 'warning' | ''): { border: string; text: string; shadow: string } => {

  if (status === 'error') {
    // expired -> red
    return { border: "border-semantic-error-500", text: "text-semantic-error-500", shadow: "ring-1 ring-semantic-error-500/20" };
  }
  if (status === 'warning') {
    // nearing expiry -> yellow
    return { border: "border-semantic-yellow-500", text: "text-semantic-yellow-500", shadow: "ring-1 ring-semantic-yellow-500/20" };
  }
  // good amount of time left -> green
  return { border: "border-text-success", text: "text-text-success", shadow: "ring-1 ring-text-success/33" };
};
const SubscriptionBadge = ({subscriptionData, userName, subscriptionLoading, onBand = false}: {className?: string,
    userName: string; subscriptionData: SubscriptionData|undefined, subscriptionLoading: boolean; onBand?: boolean;
  }) => {
  const { border, text, shadow } = getColorClasses(subscriptionData?.tag?.state || '');
  return (
    <div className="flex flex-col gap-1">
      <span className={onBand ? "text-[color:var(--rail-ink,var(--grey-100))] text-xs" : "text-text-high-emphasis text-xs"}>{userName}</span>
      {
        subscriptionData?.tag?.label ? 
          <motion.div 
          className="flex flex-col gap-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          >
            <Badge variant="outline" className={`rounded-sm py-0 ${subscriptionData?.tag?.label ? `${border} ${text} ${shadow}` : 'border-0 shadow-0 h-[16px]'}`}>{subscriptionData?.tag?.label || ' '}</Badge>
          </motion.div>
        : 
          subscriptionLoading
          ? <Skeleton className="h-[18px] w-[42px] rounded-sm" /> 
          : <></>
      }
    </div>
  );
};

const UserAvatarHeader = React.forwardRef<HTMLDivElement, { className?: string; userName: string; email: string, subscriptionData: SubscriptionData|undefined, subscriptionLoading: boolean, onBand?: boolean } & React.ComponentPropsWithoutRef<"div">>(
  ({ className, userName, email, subscriptionData, subscriptionLoading, onBand = false, ...props }, ref) => {


    return (
      <div className={className} ref={ref} {...props}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`flex items-center gap-2`}>
              <div className="flex items-center w-[32px] h-[32px] rounded-full justify-center bg-[var(--dash-accent,var(--primary-500))] text-[color:var(--dash-band-active-ink,var(--structural-white))]">
                {userName.charAt(0).toUpperCase()}
              </div>
              <SubscriptionBadge userName={userName} subscriptionData={subscriptionData} subscriptionLoading={subscriptionLoading} onBand={onBand}/>
            </div>
          </TooltipTrigger>
          <TooltipContent
            className="bg-primary-500 text-text-white dark:text-white"
            style={{ "--dash-tooltip-arrow": "var(--primary-500)" } as React.CSSProperties}
          >
            <p>{email}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }
);

UserAvatarHeader.displayName = "UserAvatarHeader";